// Chakra imports
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Avatar,
  Badge,
  Button,
  useColorModeValue,
  useBreakpointValue,
  Divider,
  IconButton,
} from "@chakra-ui/react";
import { ItemContent } from "../Menu/ItemContent";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { CloseIcon, CheckIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  deleteNotification,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../services/notificationsApi";
import {
  getPerformanceSummaryPreviewText,
  isPerformanceSummaryEventKey,
  resolveNotificationDisplayTitle,
} from "../../utils/notificationDisplay";
import NotificationListItem from "./NotificationListItem";
import NotificationDetailPanel from "./NotificationDetailPanel";
import type { NotificationModel as Notification } from "./notificationModels";
import { getNotificationColor, getNotificationIcon } from "./notificationModels";
import { useHistory } from "react-router-dom";

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  notifications?: Notification[];
  onUnreadCountChange?: (count: number) => void;
  /** When opening from a dropdown row, select this notification in the detail pane (once per open). */
  initialFocusNotificationId?: string | null;
}

const EMPTY_NOTIFICATIONS: Notification[] = [];

function formatTimeAgo(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  notifications,
  onUnreadCountChange,
  initialFocusNotificationId = null,
}) => {
  const history = useHistory();
  const providedNotifications = notifications ?? EMPTY_NOTIFICATIONS;
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [notificationList, setNotificationList] = useState<Notification[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [backendUnreadCount, setBackendUnreadCount] = useState<number>(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [swipingId, setSwipingId] = useState<string | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeStartX, setSwipeStartX] = useState(0);
  const [hasSwiped, setHasSwiped] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const paginationCursorRef = useRef<string | null>(null);
  const userHasScrolledRef = useRef(false);
  /** Avoid re-selecting initial notification when `notificationList` updates after first apply. */
  const initialFocusAppliedRef = useRef(false);
  const onUnreadCountChangeRef = useRef(onUnreadCountChange);
  onUnreadCountChangeRef.current = onUnreadCountChange;
  const modalSize = useBreakpointValue({ base: "full", md: "6xl" });
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const modalBg: string = useColorModeValue("white", "#151517") as string;
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const selectedBg = useColorModeValue("rgba(56, 187, 209, 0.1)", "rgba(56, 187, 209, 0.2)");
  const brandColor = "#38BBD1";
  const textColor = useColorModeValue("gray.700", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");

  /* Mock data removed: list is loaded from GET /notifications only. On error we show empty state + retry. */
  // Initialize / load notification list (from props if provided, otherwise from backend)
  useEffect(() => {
    let isMounted = true;

    const loadFromBackend = async () => {
      setLoadError(false);
      try {
        const { notifications: items, nextCursor, unreadCount, hasMore: more } = await fetchNotifications({ limit: 50 });
        if (!isMounted) return;

        const mapped: Notification[] = items.map((n) => ({
          id: n.id,
          type: (n.category as Notification["type"]) ?? "system",
          title: resolveNotificationDisplayTitle(n.title, n.eventKey ?? null, n.data),
          message: n.bodyMarkdown,
          time: formatTimeAgo(n.createdAt),
          avatarName: "Tessera",
          avatarSrc: "",
          isRead: n.statusInApp === "read",
          eventKey: n.eventKey ?? null,
          details: n.data as Notification["details"],
        }));

        setNotificationList(mapped);
        const uc = typeof unreadCount === "number" ? unreadCount : 0;
        setBackendUnreadCount(uc);
        onUnreadCountChangeRef.current?.(uc);
        const cursor = nextCursor ?? null;
        paginationCursorRef.current = cursor;
        setNextCursor(cursor);
        setHasMore(more ?? Boolean(cursor));
        userHasScrolledRef.current = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load notifications for modal:", error);
        if (isMounted) {
          setNotificationList([]);
          setLoadError(true);
          paginationCursorRef.current = null;
          setNextCursor(null);
          setHasMore(false);
          setBackendUnreadCount(0);
        }
      }
    };

    if (providedNotifications.length > 0) {
      setNotificationList(providedNotifications);
      setLoadError(false);
    } else if (isOpen) {
      loadFromBackend();
    }

    return () => {
      isMounted = false;
    };
  }, [providedNotifications, isOpen, reloadTrigger]);

  const loadNextPage = useCallback(async () => {
    if (!isOpen) return;
    if (providedNotifications.length > 0) return;
    if (isLoadingMore) return;
    if (!hasMore) return;
    const cursor = paginationCursorRef.current;
    if (!cursor) return;

    setIsLoadingMore(true);
    try {
      const { notifications: items, nextCursor, hasMore: more } = await fetchNotifications({
        limit: 50,
        cursor,
      });
      const mapped: Notification[] = items.map((n) => ({
        id: n.id,
        type: (n.category as Notification["type"]) ?? "system",
        title: resolveNotificationDisplayTitle(n.title, n.eventKey ?? null, n.data),
        message: n.bodyMarkdown,
        time: formatTimeAgo(n.createdAt),
        avatarName: "Tessera",
        avatarSrc: "",
        isRead: n.statusInApp === "read",
        eventKey: n.eventKey ?? null,
        details: n.data as Notification["details"],
      }));
      setNotificationList((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const deduped = mapped.filter((x) => !seen.has(x.id));
        return prev.concat(deduped);
      });
      const cursorNext = nextCursor ?? null;
      paginationCursorRef.current = cursorNext;
      setNextCursor(cursorNext);
      setHasMore((more ?? Boolean(cursorNext)) && items.length > 0);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, isOpen, providedNotifications.length]);

  const handleListScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollTop > 0) userHasScrolledRef.current = true;
    // Only paginate once the user has actually scrolled.
    if (!userHasScrolledRef.current) return;
    // Only paginate if we are close to bottom.
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining > 300) return;
    void loadNextPage();
  }, [loadNextPage]);

  // Proactive realtime updates from multiplexed /ws/signals notifications.
  useEffect(() => {
    const onWsNotification = (evt: Event) => {
      const detail = (evt as CustomEvent).detail as { row?: any };
      const row = detail?.row;
      if (!row || !row.id) return;

      const createdAtIso = String(row.created_at ?? row.createdAt ?? new Date().toISOString());
      const next: Notification = {
        id: String(row.id),
        type: (row.category as Notification["type"]) ?? "system",
        title: String(row.title ?? "Notification"),
        message: String(row.body_markdown ?? row.bodyMarkdown ?? ""),
        time: createdAtIso,
        avatarName: "Tessera",
        avatarSrc: "",
        isRead: String(row.status_in_app ?? row.statusInApp ?? "unread") === "read",
        eventKey: row.event_key != null ? String(row.event_key) : null,
        details: (row.data_json ?? row.data) as Notification["details"],
      };

      setNotificationList((prev) => {
        const without = prev.filter((n) => n.id !== next.id);
        const merged = [next, ...without];

        if (!next.isRead) {
          try {
            const unread = merged.filter((n) => !n.isRead).length;
            onUnreadCountChangeRef.current?.(unread);
          } catch {
            // ignore
          }
        }

        return merged;
      });
    };

    window.addEventListener("notifications:ws", onWsNotification as EventListener);
    return () => window.removeEventListener("notifications:ws", onWsNotification as EventListener);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      initialFocusAppliedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      !isOpen ||
      !initialFocusNotificationId ||
      notificationList.length === 0 ||
      initialFocusAppliedRef.current
    ) {
      return;
    }
    const n = notificationList.find((x) => x.id === initialFocusNotificationId);
    if (!n) {
      return;
    }
    initialFocusAppliedRef.current = true;
    setSelectedNotification(n);
    if (!n.isRead) {
      setNotificationList((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))
      );
      markNotificationRead(n.id).catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Failed to mark notification as read:", error);
      });
    }
  }, [isOpen, initialFocusNotificationId, notificationList]);

  const handleModalClose = () => {
    setSelectedNotification(null);
    onClose();
  };

  // Swipe handlers for mobile
  const handleTouchStart = (e: React.TouchEvent, notificationId: string) => {
    if (!isMobile) return;
    e.stopPropagation();
    setSwipeStartX(e.touches[0].clientX);
    setSwipingId(notificationId);
    setHasSwiped(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !swipingId) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - swipeStartX;
    const absDiff = Math.abs(diff);
    
    // Only prevent default if we're actually swiping horizontally (more horizontal than vertical)
    // This allows vertical scrolling to work normally
    if (absDiff > 5) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Limit swipe to max 120px
    const maxSwipe = 120;
    const offset = Math.max(-maxSwipe, Math.min(maxSwipe, diff));
    setSwipeOffset(offset);
    // Mark as swiped if moved more than 10px
    if (absDiff > 10) {
      setHasSwiped(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile || !swipingId) return;
    e.stopPropagation();
    
    const threshold = 60; // Minimum swipe distance to trigger action
    
    if (swipeOffset < -threshold) {
      // Swipe left - mark as read
      handleMarkAsRead(swipingId);
    } else if (swipeOffset > threshold) {
      // Swipe right - delete
      handleDelete(swipingId);
    }
    
    // Reset swipe state
    setTimeout(() => {
      setSwipeOffset(0);
      setSwipingId(null);
      setSwipeStartX(0);
      setHasSwiped(false);
    }, 200);
  };

  const handleMarkAsRead = (id: string) => {
    setNotificationList((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    // Fire-and-forget backend update
    markNotificationRead(id).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("Failed to mark notification as read:", error);
    });
  };

  const handleDelete = (id: string) => {
    setNotificationList((prev) => prev.filter((n) => n.id !== id));
    if (selectedNotification?.id === id) {
      setSelectedNotification(null);
    }
    // Fire-and-forget backend delete
    deleteNotification(id).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("Failed to delete notification:", error);
    });
  };

  // Use backend GLOBAL unread_count for header + "Mark all read" visibility.
  const unreadCount = backendUnreadCount;


  const handleNotificationClick = (notification: Notification) => {
    const eventKey = notification.eventKey ?? "";
    const details = notification.details ?? null;
    const isAutoTrade =
      eventKey === "automated_strategy.trade_executed" || eventKey === "automated_strategy.trade_closed";

    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    if (isAutoTrade && details && typeof details === "object") {
      const d = details as Record<string, unknown>;
      const actionUrl = (typeof d.action_url === "string" && d.action_url.trim()) ? d.action_url.trim() : null;
      const tradeLogUrl = (typeof d.trade_log_url === "string" && d.trade_log_url.trim()) ? d.trade_log_url.trim() : null;
      const tradeId = (typeof d.trade_id === "string" && d.trade_id.trim()) ? d.trade_id.trim() : null;

      const candidate = actionUrl || tradeLogUrl || (tradeId ? `/trade-log?tradeId=${encodeURIComponent(tradeId)}` : null);
      if (candidate) {
        // Convert full URLs (from APP_ORIGIN) into a relative path for in-app navigation.
        let path = candidate;
        try {
          if (/^https?:\/\//i.test(candidate)) {
            const u = new URL(candidate);
            path = `${u.pathname}${u.search}${u.hash || ""}`;
          }
        } catch {
          // keep candidate as-is
        }
        onClose();
        history.push(path);
        return;
      }
    }

    setSelectedNotification(notification);
  };

  const handleMarkAllRead = () => {
    void markAllNotificationsRead()
      .then(() => {
        setNotificationList((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setBackendUnreadCount(0);
        onUnreadCountChangeRef.current?.(0);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Failed to mark all notifications read:", error);
        // Fallback: mark loaded items read
        setNotificationList((prev) => {
          prev.forEach((n) => {
            if (!n.isRead) {
              markNotificationRead(n.id).catch(() => null);
            }
          });
          return prev.map((n) => ({ ...n, isRead: true }));
        });
      });
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleModalClose} 
      size={modalSize}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      {/* @ts-ignore - Chakra UI complex union type limitation */}
      <ModalContent
        bg={modalBg}
        maxH={{ base: "95vh", md: "90vh" }}
        maxW={{ base: "95%", md: "90%" }}
        borderRadius={{ base: "lg", md: "lg" }}
        m={{ base: 4, md: "auto" }}
        my={{ base: 4, md: 8 }}
      >
        <ModalHeader
          pt={{ base: "16px", md: "24px" }}
          px={{ base: "16px", md: "24px" }}
          pb={{ base: "12px", md: "16px" }}
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Flex
            justifyContent="space-between"
            alignItems="center"
            direction="row"
            gap={4}
          >
            <HStack spacing={3} flexWrap="wrap">
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="500"
                color={brandColor}
                fontFamily="'Urbanist', sans-serif"
              >
                Notifications
              </Text>
              {unreadCount > 0 && (
                <Box
                  bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                  color="white"
                  fontWeight="medium"
                  borderRadius="8px"
                  px={{ base: "16px", md: "20px" }}
                  py={{ base: "8px", md: "10px" }}
                  h="auto"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textTransform="uppercase"
                  fontSize={{ base: "xs", md: "sm" }}
                  display="inline-flex"
                  alignItems="center"
                >
                  <Text fontSize={{ base: "xs", md: "sm" }} color="white" fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
                    {unreadCount} New
                  </Text>
                </Box>
              )}
            </HStack>
            <HStack spacing={2} flexShrink={0}>
              {unreadCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  fontSize="xs"
                  fontWeight="500"
                  color={secondaryTextColor}
                  onClick={handleMarkAllRead}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textTransform="uppercase"
                >
                  Mark all read
                </Button>
              )}
              <ModalCloseButton position="static" />
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalBody p={0}>
          <Flex
            h={{ base: "calc(100vh - 120px)", md: "calc(90vh - 100px)" }}
            direction={{ base: "column", md: "row" }}
          >
            {/* Left Column - Notification List */}
            <Box
              ref={scrollContainerRef}
              w={{ base: "100%", md: selectedNotification ? "50%" : "100%" }}
              borderRight={{ base: "none", md: selectedNotification ? "1px solid" : "none" }}
              borderBottom={{ base: selectedNotification ? "1px solid" : "none", md: "none" }}
              borderColor={borderColor}
              overflowY="auto"
              maxH={{ base: selectedNotification ? "50vh" : "calc(100vh - 120px)", md: "calc(90vh - 100px)" }}
              flexShrink={0}
              style={{ scrollBehavior: "smooth" }}
              onScroll={handleListScroll}
            >
              <VStack spacing={0} align="stretch">
                {notificationList.length === 0 ? (
                  <Box p={8} textAlign="center">
                    {loadError ? (
                      <VStack spacing={3}>
                        <Text color={secondaryTextColor} fontFamily="'Urbanist', sans-serif">
                          Couldn&apos;t load notifications. Check your connection and try again.
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="cyan"
                          onClick={() => setReloadTrigger((t) => t + 1)}
                        >
                          Try again
                        </Button>
                      </VStack>
                    ) : (
                      <Text color={secondaryTextColor} fontFamily="'Urbanist', sans-serif">
                        No notifications
                      </Text>
                    )}
                  </Box>
                ) : (
                  notificationList.map((notification, index) => {
                    const isSwiping = swipingId === notification.id;
                    const showReadAction = isSwiping && swipeOffset < -20;
                    const showDeleteAction = isSwiping && swipeOffset > 20;

                    return (
                      <Box key={notification.id} position="relative" overflow="hidden" bg={modalBg}>                        {/* Swipe action buttons - behind the card */}
                        {isMobile && (
                          <Flex
                            position="absolute"
                            left={0}
                            top={0}
                            bottom={0}
                            w="120px"
                            align="center"
                            justify="flex-end"
                            pr={4}
                            bg="green.500"
                            opacity={showReadAction ? 1 : 0}
                            transition="opacity 0.2s"
                            zIndex={0}
                            pointerEvents={showReadAction ? "auto" : "none"}
                          >
                            <IconButton
                              aria-label="Mark as read"
                              icon={<CheckIcon />}
                              size="sm"
                              colorScheme="green"
                              variant="solid"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notification.id);
                                setSwipingId(null);
                                setSwipeOffset(0);
                              }}
                            />
                          </Flex>
                        )}
                        {isMobile && (
                          <Flex
                            position="absolute"
                            right={0}
                            top={0}
                            bottom={0}
                            w="120px"
                            align="center"
                            justify="flex-start"
                            pl={4}
                            bg="red.500"
                            opacity={showDeleteAction ? 1 : 0}
                            transition="opacity 0.2s"
                            zIndex={0}
                            pointerEvents={showDeleteAction ? "auto" : "none"}
                          >
                            <IconButton
                              aria-label="Delete"
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="solid"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(notification.id);
                                setSwipingId(null);
                                setSwipeOffset(0);
                              }}
                            />
                          </Flex>
                        )}
                        {/* Notification card - slides over the action buttons */}
                        <Box
                          px={4}
                          py={3}
                          cursor="pointer"
                          bg={
                            selectedNotification?.id === notification.id
                              ? selectedBg
                              : !notification.isRead
                              ? hoverBg
                              : modalBg
                          }
                          borderLeft={
                            selectedNotification?.id === notification.id
                              ? `3px solid ${brandColor}`
                              : "3px solid transparent"
                          }
                          _hover={{
                            bg: selectedNotification?.id === notification.id ? selectedBg : hoverBg,
                          }}
                          onClick={() => {
                            if (!hasSwiped && !isSwiping && swipeOffset === 0) {
                              handleNotificationClick(notification);
                            }
                          }}
                          onTouchStart={(e) => handleTouchStart(e, notification.id)}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                          transform={isSwiping ? `translateX(${swipeOffset}px)` : "translateX(0)"}
                          transition={isSwiping ? "none" : "transform 0.2s ease-out"}
                          position="relative"
                          zIndex={1}
                        >
                          <NotificationListItem
                            item={{
                              id: notification.id,
                              title: notification.title,
                              messageHtml: notification.message || "",
                              previewText: isPerformanceSummaryEventKey(notification.eventKey)
                                ? getPerformanceSummaryPreviewText(notification.details)
                                : undefined,
                              time: notification.time,
                              isRead: notification.isRead,
                              icon: getNotificationIcon(notification.type),
                            }}
                            brandColor={brandColor}
                            secondaryTextColor={secondaryTextColor}
                            background="transparent"
                            hoverBackground={
                              selectedNotification?.id === notification.id ? selectedBg : hoverBg
                            }
                            selected={selectedNotification?.id === notification.id}
                            selectedBackground={selectedBg}
                            showUnreadDot
                            showDeleteButton
                            onClick={() => {
                              if (!hasSwiped && !isSwiping && swipeOffset === 0) {
                                handleNotificationClick(notification);
                              }
                            }}
                            onDelete={() => handleDelete(notification.id)}
                          />
                        </Box>
                        {index < notificationList.length - 1 && (
                          <Divider borderColor={borderColor} />
                        )}
                      </Box>
                    );
                  })
                )}
                {/* "No more" only when backend says no next page */}
                {notificationList.length > 0 && !hasMore && (
                  <Box p={4} textAlign="center">
                    <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                      No more notifications
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Right Column - Notification Details */}
            {selectedNotification && (
              <NotificationDetailPanel
                notification={selectedNotification}
                onDelete={handleDelete}
                onClose={() => setSelectedNotification(null)}
              />
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NotificationModal;

